import { Container, Typography } from '@evocolabs/orkaui'
import { Box, Stack, useMediaQuery } from '@mui/material'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { ContentTitle, SectionTitle } from '../title'
import { default as ImageCard, CardInfo } from '../ImageCard'
import styles from './styles.module.scss'
import { getImage } from '@/src/utils/media'
import theme from '@/src/theme'

const SRC_PATH = 'website/orkaCare'

const data: {
  title: string
  cardData: Array<CardInfo>
} = {
  title: 'Once you decide on Orka, we will handle the rest.',
  cardData: [
    {
      title: 'Welcome Call',
      content:
        'We will be in touch and walk you through the basics of hearing aids and hearing health.',
      src: 'WelcomeCall.jpg'
    },
    {
      title: 'Fine-Tuning Orka Two',
      content:
        'We will customize your hearing aids and better fit your unique hearing needs.',
      src: 'OrkaTwo.jpg'
    },
    {
      title: 'Ongoing Care',
      content: `We are always on standby to address your concerns in the convenience of your home—
      without getting in the car, braving traffic, or missing work.`,
      src: 'OngoingCare.jpg'
    }
  ]
}

const AfterSalesMobile = () => (
  <Stack spacing={5}>
    {data.cardData.map(({ src, ...props }) => (
      <ImageCard
        src={getImage(src, 'PC', SRC_PATH)}
        {...props}
        key={`${props.title}-mobile`}
      />
    ))}
  </Stack>
)

const AfterSalesTablet = () => (
  <Stack spacing={3}>
    {data.cardData.map(({ src, ...props }) => (
      <ImageCard
        src={getImage(src, 'PC', SRC_PATH)}
        horizontal
        spacing={3}
        {...props}
        key={`${props.title}-tablet`}
      />
    ))}
  </Stack>
)

const AfterSalesDesktop = () => {
  const { cardData } = data
  const [currPos, setCurrPos] = useState<number>(0)

  useEffect(() => {
    const intervalID = setInterval(() => {
      setCurrPos((currPos) => (currPos + 1) % 3)
    }, 3000)

    return () => clearInterval(intervalID)
  }, [])

  return (
    <Stack direction="row" spacing={8} mt={6}>
      <Stack flex="1 1 0" spacing={5} alignSelf="center">
        {cardData.map(({ title, content }, index) => (
          <Stack direction="row" spacing={2} key={`${title}-desktop`}>
            <Box>
              <Box
                className={`${styles.tab} ${
                  currPos === index ? styles.tab_active : ''
                }`}
              />
            </Box>
            <Stack>
              <ContentTitle
                textAlign="left"
                style={{
                  color: theme.palette.grey[currPos === index ? 800 : 400]
                }}
              >
                {title}
              </ContentTitle>
              <Typography
                mt={1}
                variant="body2"
                style={{
                  color: theme.palette.grey[currPos === index ? 700 : 400]
                }}
              >
                {content}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Box position="relative" paddingBottom="50%" flex="1 1 0">
        {cardData.map(({ src, title }, index) => (
          <Image
            src={getImage(src, 'PC', SRC_PATH)}
            alt={title}
            key={title}
            fill
            sizes="(min-width: 1024px) 100%"
            unoptimized
            style={{
              borderRadius: '12px',
              objectFit: 'cover',
              display: currPos === index ? 'block' : 'none'
            }}
          />
        ))}
      </Box>
    </Stack>
  )
}

const AfterSales = () => {
  const isMobile = useMediaQuery('(max-width:600px)')
  const isTablet = useMediaQuery('(max-width:1024px)')

  return (
    <Box bgcolor={theme.palette.secondary[100]} mt={{ xs: 7, dl: 3 }}>
      <Container
        sx={{
          paddingY: { xs: 8, dl: 10 }
        }}
      >
        <SectionTitle mb={{ xs: 5, dl: 6 }}>{data.title}</SectionTitle>
        {isMobile ? (
          <AfterSalesMobile />
        ) : isTablet ? (
          <AfterSalesTablet />
        ) : (
          <AfterSalesDesktop />
        )}
      </Container>
    </Box>
  )
}

export default AfterSales
